// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — pierStalls.jsx   ·   THE STALLS, THE KEEPER, THE ENDS
// ────────────────────────────────────────────────────────────────────
// Boardwalk Bazaar · Mystic Ally's wagon · W'ily's post · the Weathered
// Sign · the Gate · the thing at the Far End.
//
// Each takes `{ n }`, its INTERACTABLES row (the gate takes the world),
// and faces the way its row says people approach it. Same laws as the
// rest of the kit: zero lights, merged by material, one driver.
// ════════════════════════════════════════════════════════════════════

import { Component, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useLoader } from "@react-three/fiber";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";
import * as THREE from "three";
import { Merger, boxUV, canvasTex, mulberry32, textTex, FONT_GEN } from "./pierTex";
import { timberTex, clapTex, awningTex, signTex, poolTex, dotTex, shadowTex } from "./pierPaint";
import { Merged, BUILD_TICKS, useOccluder } from "./pierBuild";
import { Glows } from "./pierPieces";
import { listenToLedger } from "./bazaar";
import { CURRENCIES, DC_ID, tintFor } from "./progression";
import { SHELVES } from "./markVariants";
import { BonnieByTheWagon } from "./pierFolk3D";
import { FlamingSeal } from "./greenFlame";

const FONT = "Cinzel, 'Palatino Linotype', serif";

function useTick(fn, deps) {
  useEffect(() => {
    BUILD_TICKS.add(fn);
    return () => { BUILD_TICKS.delete(fn); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
const disposeGeos = (g) => Object.values(g || {}).forEach((x) => x.dispose());

const stripeTex = (a, b) => canvasTex(`bw-stripe|${a}|${b}`, 256, (ctx, S) => {
  const n = 8;
  for (let i = 0; i < n; i++) { ctx.fillStyle = i % 2 ? b : a; ctx.fillRect((i * S) / n, 0, S / n, S); }
  const g = ctx.createLinearGradient(0, 0, 0, S);
  g.addColorStop(0, "rgba(255,255,255,0.12)"); g.addColorStop(1, "rgba(0,0,0,0.3)");
  ctx.fillStyle = g; ctx.fillRect(0, 0, S, S);
});

const shadowMat = () => new THREE.MeshBasicMaterial({ map: shadowTex(), transparent: true, depthWrite: false, opacity: 0.8 });

// ════════════════════════════════════════════════════════════════════
// ⭐ THE BOARDWALK BAZAAR — Wyllie's tent, and the Market Pulse over it
// ────────────────────────────────────────────────────────────────────
// A striped canvas stall in the market square. The board over the
// counter is LIVE: it reads the Bazaar's ledger and turns over the latest
// sales, so you see trade happening before you reach the counter. It
// repaints ONE canvas when the sale changes — never per frame.
// ════════════════════════════════════════════════════════════════════
const ROSE = "#f472b6";

function usePulseBoard() {
  const [sales, setSales] = useState([]);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let off = null;
    try { off = listenToLedger((s) => setSales(Array.isArray(s) ? s : []), 12); } catch { /* signed out: the board just waits */ }
    return () => { try { off && off(); } catch {} };
  }, []);
  useEffect(() => {
    if (sales.length <= 1) return undefined;
    const t = setInterval(() => setTick((k) => k + 1), 3200);
    return () => clearInterval(t);
  }, [sales.length]);

  const tex = useMemo(() => {
    const cv = document.createElement("canvas");
    cv.width = 1024; cv.height = 256;
    const t = new THREE.CanvasTexture(cv);
    t.colorSpace = THREE.SRGBColorSpace;
    t.anisotropy = 8;
    return t;
  }, []);
  useEffect(() => () => tex.dispose(), [tex]);

  const current = sales.length ? sales[tick % sales.length] : null;
  useEffect(() => {
    const cv = tex.image, ctx = cv.getContext("2d"), W = cv.width, H = cv.height;
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, "#1c0e22"); g.addColorStop(1, "#0a0512");
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = ROSE; ctx.lineWidth = 8; ctx.strokeRect(10, 10, W - 20, H - 20);
    ctx.textBaseline = "middle";
    ctx.beginPath(); ctx.arc(60, 64, 12, 0, 7); ctx.fillStyle = "#4ade80";
    ctx.shadowColor = "#4ade80"; ctx.shadowBlur = 16; ctx.fill(); ctx.shadowBlur = 0;
    ctx.font = `900 62px ${FONT}`; ctx.fillStyle = ROSE; ctx.textAlign = "left";
    ctx.shadowColor = ROSE; ctx.shadowBlur = 18;
    ctx.fillText("MARKET PULSE · LIVE", 90, 66); ctx.shadowBlur = 0;
    ctx.font = `700 78px ${FONT}`;
    if (current) {
      const glyph = CURRENCIES[current.currency]?.icon || "•";
      const color = tintFor(current.currency || DC_ID);
      const title = String(current.title || "A piece").slice(0, 15);
      ctx.fillStyle = "#f0e6d0"; ctx.fillText(title, 48, 170);
      const price = `${glyph} ${Number(current.price || 0).toLocaleString()}`;
      ctx.textAlign = "right"; ctx.fillStyle = color; ctx.fillText(price, W - 48, 170);
    } else {
      ctx.fillStyle = "#9a8a7a"; ctx.font = `italic 600 64px ${FONT}`;
      ctx.fillText("awaiting its first sale…", 48, 170);
    }
    tex.needsUpdate = true;
    // FONT_GEN: repaint once the real face lands
  }, [current, tex]);
  return tex;
}

export function BazaarStall({ n }) {
  const built = useMemo(() => {
    const m = new Merger();
    // local: front +z; the square's stall faces the spine (south)
    m.box("timber", 2.9, 0.92, 0.75, [0, 0.46, 0.5]);                 // counter
    m.box("top", 3.1, 0.08, 0.92, [0, 0.96, 0.5]);
    m.box("timber", 2.9, 1.7, 0.36, [0, 0.85, -0.62]);                // back shelves
    for (const y of [0.55, 1.05, 1.5]) m.box("top", 2.9, 0.05, 0.45, [0, y, -0.4]);
    for (const [px, pz] of [[-1.5, 0.9], [1.5, 0.9], [-1.5, -0.85], [1.5, -0.85]]) {
      m.cyl("pole", 0.05, 0.06, 2.55, 8, [px, 1.275, pz]);
    }
    // a rectangular peaked canopy: the square pyramid is turned square-on
    // FIRST, then flattened front-to-back to the stall's own depth
    m.add("canvas", new THREE.CylinderGeometry(0.02, 2.3, 0.95, 4, 1).rotateY(Math.PI / 4),
      [0, 2.98, 0.02], [0, 0, 0], [1, 1, 0.62]);
    m.box("pole", 3.1, 0.08, 0.08, [0, 2.5, 0.9]);
    m.box("pole", 3.1, 0.08, 0.08, [0, 2.5, -0.85]);
    // the Pulse board hangs from the front pole on two cords, in a frame —
    // without them it was a picture floating under the canopy
    for (const sx of [-1.05, 1.05]) m.box("pole", 0.025, 0.2, 0.025, [sx, 2.4, 0.95]);
    m.box("timber", 2.6, 0.72, 0.04, [0, 2.02, 0.935]);
    // crates and a barrel beside the counter
    m.box("timber", 0.55, 0.5, 0.55, [-1.9, 0.25, 0.7], [0, 0.3, 0]);
    m.box("timber", 0.45, 0.4, 0.45, [-1.85, 0.7, 0.66], [0, -0.2, 0]);
    m.cyl("timber", 0.28, 0.25, 0.7, 12, [1.95, 0.35, 0.55]);
    const geos = m.build();
    ["timber", "top", "canvas"].forEach((k) => geos[k] && boxUV(geos[k], 1.2));
    // the wares on the shelves: small framed pieces of Empire art
    const R = mulberry32(0xBA2A);
    const art = Array.from({ length: 7 }, (_, i) => ({
      x: -1.2 + (i % 4) * 0.8 + (R() - 0.5) * 0.1,
      y: i < 4 ? 1.25 : 0.78,
      h: 0.3 + R() * 0.12,
      hue: Math.floor(R() * 360),
    }));
    return { geos, art };
  }, []);

  const pulse = usePulseBoard();
  const mats = useMemo(() => ({
    timber: new THREE.MeshStandardMaterial({ map: timberTex(), color: "#8a6a48", roughness: 1 }),
    top: new THREE.MeshStandardMaterial({ map: timberTex(), color: "#c09a6a", roughness: 0.8 }),
    pole: new THREE.MeshStandardMaterial({ color: "#2a1e16", roughness: 0.8 }),
    canvas: new THREE.MeshStandardMaterial({ map: stripeTex(ROSE, "#efe2c8"), roughness: 0.95, side: THREE.DoubleSide, emissive: "#3a1026", emissiveIntensity: 0.35 }),
    valance: new THREE.MeshStandardMaterial({ map: awningTex(ROSE), transparent: true, alphaTest: 0.35, side: THREE.DoubleSide, roughness: 0.9 }),
    board: new THREE.MeshBasicMaterial({ map: pulse, toneMapped: false }),
    sign: new THREE.MeshBasicMaterial({ map: textTex("BOARDWALK BAZAAR", ROSE), transparent: true, depthWrite: false, toneMapped: false }),
    shadow: shadowMat(),
  }), [pulse]);
  useEffect(() => () => disposeGeos(built.geos), [built]);

  const ref = useRef();
  // shared, so the fader can reach them (an inline JSX material cannot be faded)
  const artMats = useMemo(() => built.art.map((a) => new THREE.MeshStandardMaterial({
    color: `hsl(${a.hue},55%,52%)`, emissive: `hsl(${a.hue},70%,30%)`, emissiveIntensity: 0.6, roughness: 0.5,
  })), [built]);
  useEffect(() => () => artMats.forEach((m) => m.dispose()), [artMats]);
  const mass = useMemo(() => new THREE.Box3(new THREE.Vector3(-1.6, 0, -1), new THREE.Vector3(1.6, 3.5, 1)), []);
  const occ = useOccluder(ref, mass, [mats.timber, mats.top, mats.canvas, mats.valance, mats.pole, mats.board, mats.sign, mats.shadow, ...artMats], [mats, artMats]);

  const glows = useMemo(() => [
    { x: n.x - 1.5, y: 2.3, z: n.y + 0.9, color: "#ffb0d8", size: 1 },
    { x: n.x + 1.5, y: 2.3, z: n.y + 0.9, color: "#ffb0d8", size: 1 },
  ], [n.x, n.y]);

  return (
    <group>
      <group ref={ref} position={[n.x, 0, n.y]}>
        <mesh position={[0, 0.015, 0]} rotation={[-Math.PI / 2, 0, 0]} material={mats.shadow}>
          <planeGeometry args={[4.2, 2.8]} />
        </mesh>
        <Merged geos={built.geos} mats={mats} />
        {[0.93, -0.88].map((z) => (
          <mesh key={z} position={[0, 2.3, z]} material={mats.valance}>
            <planeGeometry args={[3.15, 0.42]} />
          </mesh>
        ))}
        <mesh position={[0, 2.02, 0.96]} material={mats.board}>
          <planeGeometry args={[2.5, 0.62]} />
        </mesh>
        <mesh position={[0, 0.58, 0.9]} material={mats.sign}>
          <planeGeometry args={[2.8, 0.44]} />
        </mesh>
        {built.art.map((a, i) => (
          <mesh key={i} position={[a.x, a.y, -0.42]} material={artMats[i]}>
            <planeGeometry args={[a.h * 0.8, a.h]} />
          </mesh>
        ))}
      </group>
      <Glows points={glows} size={1.6} occ={occ} />
    </group>
  );
}

// ════════════════════════════════════════════════════════════════════
// ⭐ THE CODE WAGON — the FAIL-SAFE for Bonnie's Blender vardo (below). It stands
// while bonnie_wagon.glb loads, if it fails, or with ?wagon=code. Was Mystic Ally's.
// ────────────────────────────────────────────────────────────────────
// A violet caravan with its side let down into a counter, and THE THREE
// SHELVES hanging in it as set dressing: the left plate flat and honest,
// the middle elaborate and PERFECTLY STILL, the right one the middle one
// awake. A player who never opens the shop still learns the shelves by
// walking past. (Law carried over from the 2D stall, unchanged.)
// ════════════════════════════════════════════════════════════════════
const VIOLET = "#a78bfa";

const plateTex = (kind, tint, hi) => canvasTex(`bw-plate|${kind}|${tint}`, 128, (ctx, W, H) => {
  ctx.fillStyle = "#0b0912"; ctx.fillRect(0, 0, W, H);
  if (kind === "fable") {
    const g = ctx.createLinearGradient(0, 0, W, H);
    g.addColorStop(0.2, "#0b0912"); g.addColorStop(0.5, tint); g.addColorStop(0.8, "#0b0912");
    ctx.globalAlpha = 0.7; ctx.fillStyle = g; ctx.fillRect(0, 0, W, H); ctx.globalAlpha = 1;
  } else {
    ctx.save(); ctx.translate(W / 2, H * 0.1);
    for (let a = 0; a < 72; a++) {
      ctx.rotate((Math.PI * 2) / 72);
      ctx.fillStyle = a % 2 ? tint + "55" : "transparent";
      ctx.fillRect(0, 0, W * 2, 3);
    }
    ctx.restore();
    const g = ctx.createRadialGradient(W * 0.2, 0, 0, W * 0.2, 0, H);
    g.addColorStop(0, hi + "88"); g.addColorStop(1, "transparent");
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
  }
  ctx.strokeStyle = tint; ctx.lineWidth = 4; ctx.strokeRect(2, 2, W - 4, H - 4);
}, 176);

function CodeWagon({ n }) {
  const built = useMemo(() => {
    const m = new Merger();
    // local: front +z (the let-down side). The group turns it to face the spine.
    // ⭐ A VARDO, NOT A BOX (visual review, 2026-09-14: "a purple dumpster").
    // A planked body up on a chassis, a bowed canvas roof over ribs, spoked
    // wheels, and the shafts it was drawn in by — and lower than the old block.
    m.box("chassis", 3.1, 0.14, 1.6, [0, 0.64, -0.2]);
    m.box("body", 2.9, 1.2, 1.6, [0, 1.3, -0.2]);
    m.box("trim", 3.02, 0.1, 1.7, [0, 1.93, -0.2]);                    // eave
    m.box("trim", 3.02, 0.1, 1.7, [0, 0.76, -0.2]);                    // sill
    for (const sx of [-1, 1]) m.box("trim", 0.09, 1.2, 1.66, [sx * 1.45, 1.3, -0.2]);
    const BOW = 0.9, SQ = 0.62;                                        // the bow's radius, and how flat it sits
    m.add("canvas", new THREE.CylinderGeometry(BOW, BOW, 3.08, 20, 1, true, 0, Math.PI), [0, 1.97, -0.2], [0, 0, Math.PI / 2], [SQ, 1, 1]);
    for (const sx of [-1, 1]) m.add("body", new THREE.CircleGeometry(BOW, 16, 0, Math.PI), [sx * 1.5, 1.97, -0.2], [0, sx * Math.PI / 2, 0], [1, SQ, 1]);
    for (const rx of [-1.5, -0.5, 0.5, 1.5]) m.add("trim", new THREE.TorusGeometry(BOW, 0.035, 5, 18, Math.PI), [rx, 1.97, -0.2], [0, Math.PI / 2, 0], [1, SQ, 1]);
    m.box("dark", 2.4, 0.95, 0.1, [0, 1.3, 0.66]);                     // the hatch
    m.box("counter", 2.6, 0.08, 0.55, [0, 0.86, 1.08]);                // let-down side
    m.box("trim", 0.06, 0.52, 0.06, [-1.2, 0.58, 1.3]);
    m.box("trim", 0.06, 0.52, 0.06, [1.2, 0.58, 1.3]);
    for (const sx of [-1, 1]) for (const wz of [-1.12, 0.72]) {
      const at = [sx * 1.0, 0.49, wz];
      m.add("wheel", new THREE.TorusGeometry(0.44, 0.045, 6, 20), at);                               // rim
      m.add("wheel", new THREE.CylinderGeometry(0.09, 0.09, 0.12, 10), at, [Math.PI / 2, 0, 0]);     // hub
      for (let k = 0; k < 6; k++) m.box("wheel", 0.04, 0.84, 0.03, at, [0, 0, (k * Math.PI) / 6]);  // spokes
    }
    for (const sz of [-0.75, 0.35]) {
      m.box("chassis", 1.7, 0.08, 0.08, [2.35, 0.55, sz], [0, 0, -0.18]);   // the shafts, resting down
      m.box("chassis", 0.06, 0.42, 0.06, [3.12, 0.21, sz]);
    }
    m.box("chassis", 0.1, 0.1, 1.2, [3.12, 0.42, -0.2]);                  // crossbar
    for (const sx of [-1, 1]) m.box("trim", 0.05, 0.4, 0.05, [sx * 1.1, 2.6, -0.21]);   // the name board's posts
    m.box("trim", 2.8, 0.6, 0.04, [0, 3.05, -0.21]);                      // and its frame
    m.cyl("trim", 0.03, 0.03, 2.4, 6, [1.45, 1.5, 0.9]);               // lantern pole
    const geos = m.build();
    ["body", "counter", "chassis"].forEach((k) => geos[k] && boxUV(geos[k], 1.1));
    return geos;
  }, []);

  const F = SHELVES.fable, L = SHELVES.legendary, M = SHELVES.mythical;
  const mats = useMemo(() => ({
    body: new THREE.MeshStandardMaterial({ map: clapTex(), color: "#6a4aa8", roughness: 0.85 }),
    canvas: new THREE.MeshStandardMaterial({ map: stripeTex("#e8dcc8", "#7c5cc4"), roughness: 0.95, side: THREE.DoubleSide, emissive: "#1a0e2a", emissiveIntensity: 0.3 }),
    chassis: new THREE.MeshStandardMaterial({ map: timberTex(), color: "#4a3626", roughness: 1 }),
    trim: new THREE.MeshStandardMaterial({ color: "#c9a84c", roughness: 0.45, metalness: 0.7, emissive: "#3a2a08", emissiveIntensity: 0.5 }),
    counter: new THREE.MeshStandardMaterial({ map: timberTex(), color: "#9a7a58", roughness: 0.8 }),
    wheel: new THREE.MeshStandardMaterial({ map: timberTex(), color: "#6a4c34", roughness: 0.9 }),
    dark: new THREE.MeshBasicMaterial({ color: "#0c0718" }),
    glow: new THREE.MeshBasicMaterial({ map: poolTex(), color: VIOLET, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false, opacity: 0.7 }),
    awn: new THREE.MeshStandardMaterial({ map: awningTex("#6d28d9"), transparent: true, alphaTest: 0.35, side: THREE.DoubleSide, roughness: 0.9 }),
    // caP'n 2026-09-24: the ONLY words on her wagon are "Bonnie's Bunni Basket" (was "The Marketplace" + "BONNIE, PROP.")
    sign: new THREE.MeshBasicMaterial({ map: signTex("Bonnie's Bunni Basket", VIOLET, false), toneMapped: false }),
    fable: new THREE.MeshBasicMaterial({ map: plateTex("fable", F.tint, F.tintHi || F.tint), toneMapped: false }),
    legend: new THREE.MeshBasicMaterial({ map: plateTex("legendary", L.tint, L.tintHi || L.tint), toneMapped: false }),
    myth: new THREE.MeshBasicMaterial({ map: plateTex("mythical", M.tint, M.tintHi || M.tint).clone(), toneMapped: false }),
    shadow: shadowMat(),
  }), [F.tint, F.tintHi, L.tint, L.tintHi, M.tint, M.tintHi]);
  useEffect(() => () => disposeGeos(built), [built]);

  // ⚑ ONLY THE MYTHICAL MOVES. Legendary is zero animation, by law.
  useTick((t) => {
    const map = mats.myth.map;
    map.center.set(0.5, 0.5);
    map.rotation = t * 0.35;
    map.needsUpdate = false;
    mats.myth.color.setScalar(0.8 + 0.2 * Math.sin(t * 2));
    mats.glow.opacity = 0.7 * (occ.current ? occ.current.k : 1);
  }, [mats]);

  const ref = useRef();
  const mass = useMemo(() => new THREE.Box3(new THREE.Vector3(-1.6, 0, -1.2), new THREE.Vector3(3.2, 3.35, 1.4)), []);
  const occ = useOccluder(ref, mass, [mats.body, mats.canvas, mats.chassis, mats.trim, mats.wheel, mats.dark, mats.sign, mats.counter, mats.awn, mats.fable, mats.legend, mats.myth, mats.shadow], [mats]);

  const rotY = Math.PI;       // the wagon's counter faces the spine, north
  const glows = useMemo(() => [
    { x: n.x - 1.45, y: 2.72, z: n.y - 0.9, color: "#d9c8ff", size: 1.1 },
    { x: n.x, y: 1.3, z: n.y - 0.7, color: VIOLET, size: 0.9 },
  ], [n.x, n.y]);

  return (
    <group>
      <group ref={ref} position={[n.x, 0, n.y]} rotation={[0, rotY, 0]}>
        <mesh position={[0.8, 0.015, -0.1]} rotation={[-Math.PI / 2, 0, 0]} material={mats.shadow}>
          <planeGeometry args={[5.4, 3.4]} />
        </mesh>
        <Merged geos={built} mats={mats} />
        <mesh position={[0, 1.3, 0.6]} material={mats.glow}>
          <planeGeometry args={[2.6, 1.3]} />
        </mesh>
        {[[mats.fable, -0.75], [mats.legend, 0], [mats.myth, 0.75]].map(([mat, x]) => (
          <mesh key={x} position={[x, 1.3, 0.72]} material={mat}>
            <planeGeometry args={[0.46, 0.64]} />
          </mesh>
        ))}
        <mesh position={[0, 1.83, 0.9]} rotation={[-1.0, 0, 0]} material={mats.awn}>
          <planeGeometry args={[2.9, 0.62]} />
        </mesh>
        <mesh position={[0, 3.05, -0.18]} material={mats.sign}>
          <planeGeometry args={[2.7, 0.53]} />
        </mesh>
        {/* ⚠ the board is painted on BOTH faces: from the default camera
            (south) you see the wagon's back, and a single-sided sign left it
            a plain purple box with no name on it */}
        <mesh position={[0, 3.05, -0.24]} rotation={[0, Math.PI, 0]} material={mats.sign}>
          <planeGeometry args={[2.7, 0.53]} />
        </mesh>
      </group>
      <Glows points={glows} size={1.8} occ={occ} />
      {/* ⭐ BONNIE keeps the wagon now (caP'n 2026-09-24): beside its west end, on the camera side */}
      <BonnieByTheWagon x={n.x + 2.0} z={n.y + 1.4} />
    </group>
  );
}

// ════════════════════════════════════════════════════════════════════
// ⭐⭐ BONNIE'S BUNNI BASKET — her gypsy wagon, built in Blender (caP'n, 2026-09-24)
// ────────────────────────────────────────────────────────────────────
// "make it a gypsy wagon but somehow its also Easter and Graveyard themed …
//  then put her in it of course so she's standing in it proper ready to sell"
// public/models/bonnie_wagon.glb — master gamemaster/models/sculpt/glb/, builder
// gamemaster/models/sculpt/builders/bonnie_wagon_blender/build_bonnie_wagon.py.
// ⚑ THE ONLY WORDS on it: "Bonnie's Bunni Basket" (a text mesh in the glb).
// ⚑ Its SERVING HATCH faces the camera (south, +z) — the old wagon's counter faced
//   the handle (north), so its keeper could only ever stand OUTSIDE it.
// ⚑ BONNIE STANDS ON ITS FLOOR at the glb's `ie_wagon.stand`, behind the counter.
// ⚑ THE THREE SHELVES (the Marketplace law) hang in the three dark headstones under
//   the counter (`ie_wagon.shelves`): Fable flat, Legendary STILL, only Mythical moves.
// Fail-safe: the code wagon above (Bonnie beside it, as before). ?wagon=code forces it.
// ════════════════════════════════════════════════════════════════════
export const BONNIE_WAGON_URL = `${process.env.PUBLIC_URL || ""}/models/bonnie_wagon.glb`;
const WAGON_MODE = typeof window !== "undefined" && /[?&]wagon=code\b/.test(window.location.search) ? "code" : "glb";

function BonnieWagonModel({ n }) {
  const gltf = useLoader(GLTFLoader, BONNIE_WAGON_URL, (l) => l.setMeshoptDecoder(MeshoptDecoder));
  const meta = useMemo(() => {
    let m = null;
    gltf.scene.traverse((o) => { if (!m && o.userData && o.userData.ie_wagon) { try { m = JSON.parse(o.userData.ie_wagon); } catch (e) { m = null; } } });
    return m || { stand: [0, 0.82, 0.3], shelves: [] };
  }, [gltf]);

  const F = SHELVES.fable, L = SHELVES.legendary, M = SHELVES.mythical;
  const built = useMemo(() => {
    const root = gltf.scene.clone(true);
    const mats = new Set();
    root.traverse((o) => {
      if (!o.isMesh) return;
      const lit = /glow_/.test(o.name) || /glow_/.test((o.parent && o.parent.name) || "");
      const arr = (Array.isArray(o.material) ? o.material : [o.material]).map((mm) => {
        const c = mm.clone();
        if (lit) { c.toneMapped = false; if (c.emissive) { c.emissive.copy(c.color); c.emissiveIntensity = 1.2; } }
        // ⚠ LIVE QA 2026-09-24: under the pier's three night lights her pastels read mud-dark and the
        // words were unreadable. Zero new lights (law), so the PAINT carries a little of its own
        // colour as glow, the words are lit like a shop sign, and gilt drops its mirror (a metal
        // with no environment to reflect renders black).
        else if (/words/.test(o.name)) { c.toneMapped = false; c.emissive && c.emissive.set("#ffd98a"); c.emissiveIntensity = 0.9; c.metalness = 0.2; }
        else if (c.emissive) { c.emissive.copy(c.color); c.emissiveIntensity = 0.28; if (c.metalness > 0.5) c.metalness = 0.35; }
        mats.add(c); return c;
      });
      o.material = arr.length === 1 ? arr[0] : arr;
    });
    const plates = [
      new THREE.MeshBasicMaterial({ map: plateTex("fable", F.tint, F.tintHi || F.tint), toneMapped: false }),
      new THREE.MeshBasicMaterial({ map: plateTex("legendary", L.tint, L.tintHi || L.tint), toneMapped: false }),
      new THREE.MeshBasicMaterial({ map: plateTex("mythical", M.tint, M.tintHi || M.tint).clone(), toneMapped: false }),
    ];
    // a warm spill inside the hatch, so she reads against the dark lining (zero lights, by law)
    const spill = new THREE.MeshBasicMaterial({ map: poolTex(), color: "#ffd9a0", transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false, opacity: 0.45 });
    const pool = new THREE.MeshBasicMaterial({ map: poolTex(), color: VIOLET, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false, opacity: 0.5 });
    return { root, mats: [...mats], plates, spill, pool };
  }, [gltf, F.tint, F.tintHi, L.tint, L.tintHi, M.tint, M.tintHi]);
  useEffect(() => () => { built.mats.forEach((m) => m.dispose()); built.plates.forEach((m) => m.dispose()); built.spill.dispose(); built.pool.dispose(); }, [built]);

  // ⚑ ONLY THE MYTHICAL MOVES. Legendary is zero animation, by law.
  useTick((t) => {
    const mm = built.plates[2], map = mm.map;
    map.center.set(0.5, 0.5); map.rotation = t * 0.35;
    mm.color.setScalar(0.8 + 0.2 * Math.sin(t * 2));
    built.spill.opacity = 0.4 + 0.06 * Math.sin(t * 1.3);
  }, [built]);

  const ref = useRef();
  // ⚠ capped under 3.6 on purpose: a wagon is a landmark, never faded (pierBuild's stall rule)
  const mass = useMemo(() => new THREE.Box3(new THREE.Vector3(-3.2, 0, -1.2), new THREE.Vector3(2.0, 3.35, 1.45)), []);
  useOccluder(ref, mass, built.mats, [built]);
  const [sx, sy, sz] = meta.stand || [0, 0.82, 0.3];
  const glows = useMemo(() => [
    { x: n.x - 1.5, y: 1.95, z: n.y + 1.0, color: "#a8ffcf", size: 0.9 },
    { x: n.x + 1.5, y: 1.95, z: n.y + 1.0, color: "#f7b8ff", size: 0.9 },
  ], [n.x, n.y]);

  return (
    <group>
      <group ref={ref} position={[n.x, 0, n.y]}>
        <primitive object={built.root} />
        <mesh position={[0, 0.02, 1.1]} rotation={[-Math.PI / 2, 0, 0]} material={built.pool}><planeGeometry args={[4.2, 2.2]} /></mesh>
        <mesh position={[0, 1.75, -0.55]} material={built.spill}><planeGeometry args={[2.0, 1.2]} /></mesh>
        {(meta.shelves || []).map(([x, y, z, w, h], i) => (
          <mesh key={i} position={[x, y, z]} material={built.plates[i]}><planeGeometry args={[w, h]} /></mesh>
        ))}
      </group>
      <Glows points={glows} size={1.4} />
      {/* ⭐ BONNIE, IN her wagon, behind the counter, facing the player */}
      <BonnieByTheWagon x={n.x + sx} y={sy} z={n.y + sz} />
    </group>
  );
}

class WagonBoundary extends Component {
  constructor(p) { super(p); this.state = { bad: false }; }
  static getDerivedStateFromError() { return { bad: true }; }
  componentDidCatch(e) { console.warn("[pier] bonnie_wagon.glb did not load — the code wagon stands in", e); }
  render() { return this.state.bad ? this.props.fallback : this.props.children; }
}

// ⭐ What Pier3D renders for the `marketplace` row.
// ⭐ caP'n 2026-09-25: a red, evil seal painted round Bonnie's Bunni Basket, with GREEN
// FLAMES (greenFlame.jsx) rising slightly from its rim. Centred on the wagon's footprint.
const SEAL = { dx: -0.6, dz: 0.1, r: 3.25 };
function WithSeal({ n, children }) {
  return (
    <group>
      <FlamingSeal radius={SEAL.r} position={[n.x + SEAL.dx, 0, n.y + SEAL.dz]} />
      {children}
    </group>
  );
}

export function AllyStall({ n }) {
  const code = <CodeWagon n={n} />;
  if (WAGON_MODE === "code") return <WithSeal n={n}>{code}</WithSeal>;
  return (
    <WithSeal n={n}>
      <WagonBoundary fallback={code}>
        <Suspense fallback={code}><BonnieWagonModel n={n} /></Suspense>
      </WagonBoundary>
    </WithSeal>
  );
}

// ════════════════════════════════════════════════════════════════════
// W'ILY'S POST — where the pier keeper stands
// ────────────────────────────────────────────────────────────────────
// W'ily has always been drawn as ambient purple sparkles, and that is
// canon — he is not given a body here that nobody designed. What changes
// is that he now has a PLACE: a keeper's lectern with the pier's ledger
// open on it and a lantern on a hook, and his sparkles turning over it.
// ════════════════════════════════════════════════════════════════════
export function KeeperPost({ n }) {
  const built = useMemo(() => {
    const m = new Merger();
    m.box("timber", 0.62, 1.05, 0.46, [0, 0.52, 0]);
    m.box("top", 0.78, 0.07, 0.6, [0, 1.1, 0.02], [-0.22, 0, 0]);
    m.box("page", 0.5, 0.03, 0.36, [0, 1.16, 0.03], [-0.22, 0, 0]);
    m.box("timber", 0.8, 0.12, 0.6, [0, 0.06, 0]);
    m.cyl("iron", 0.03, 0.035, 2.3, 6, [0.5, 1.15, -0.1]);
    m.box("iron", 0.36, 0.03, 0.03, [0.34, 2.28, -0.1]);
    m.box("glass", 0.16, 0.24, 0.16, [0.2, 2.02, -0.1]);
    const geos = m.build();
    ["timber", "top"].forEach((k) => geos[k] && boxUV(geos[k], 1.4));
    return geos;
  }, []);
  const mats = useMemo(() => ({
    timber: new THREE.MeshStandardMaterial({ map: timberTex(), color: "#7a5a3e", roughness: 1 }),
    top: new THREE.MeshStandardMaterial({ map: timberTex(), color: "#a47e56", roughness: 0.8 }),
    page: new THREE.MeshStandardMaterial({ color: "#e8d5a3", roughness: 1, emissive: "#3a2e18", emissiveIntensity: 0.4 }),
    iron: new THREE.MeshStandardMaterial({ color: "#221c2c", roughness: 0.5, metalness: 0.7 }),
    glass: new THREE.MeshBasicMaterial({ color: "#e3d6ff", toneMapped: false }),
    pool: new THREE.MeshBasicMaterial({ map: poolTex(), color: VIOLET, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false, opacity: 0.45 }),
  }), []);
  useEffect(() => () => disposeGeos(built), [built]);

  const N = 9;
  const seed = useMemo(() => {
    const R = mulberry32(0x3117);
    return Array.from({ length: N }, (_, i) => ({ a: (i / N) * 6.28, r: 0.35 + R() * 0.4, h: 1.2 + R() * 0.9, s: 0.5 + R() * 0.6, ph: R() * 6 }));
  }, []);
  const geo = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(N * 3), 3));
    return g;
  }, []);
  const mat = useMemo(() => new THREE.PointsMaterial({
    size: 0.2, map: dotTex(), color: VIOLET, transparent: true, depthWrite: false,
    blending: THREE.AdditiveBlending, toneMapped: false, fog: false,
  }), []);
  useTick((t) => {
    const a = geo.attributes.position;
    seed.forEach((s, i) => {
      const ang = s.a + t * s.s;
      a.setXYZ(i, n.x - 0.7 + Math.cos(ang) * s.r, s.h + Math.sin(t * 1.7 + s.ph) * 0.18, n.y + 0.2 + Math.sin(ang) * s.r * 0.6);
    });
    a.needsUpdate = true;
  }, [geo, seed, n.x, n.y]);
  useEffect(() => () => { geo.dispose(); mat.dispose(); }, [geo, mat]);

  const glows = useMemo(() => [{ x: n.x + 0.2, y: 2.02, z: n.y - 0.1, color: "#d9c8ff", size: 1 }], [n.x, n.y]);
  return (
    <group>
      <group position={[n.x, 0, n.y]}>
        <Merged geos={built} mats={mats} />
        <mesh position={[-0.5, 0.02, 0.2]} rotation={[-Math.PI / 2, 0, 0]} material={mats.pool}>
          <planeGeometry args={[2.4, 2.4]} />
        </mesh>
      </group>
      <points geometry={geo} material={mat} frustumCulled={false} />
      <Glows points={glows} size={1.3} />
    </group>
  );
}

// ════════════════════════════════════════════════════════════════════
// THE WEATHERED SIGN — the name W'ily would like everyone to forget
// ════════════════════════════════════════════════════════════════════
const oldSignTex = () => canvasTex(`bw-oldsign|${FONT_GEN}`, 512, (ctx, W, H) => {
  const R = mulberry32(0x01D5);
  ctx.fillStyle = "#5a4630"; ctx.fillRect(0, 0, W, H);
  for (let i = 0; i < 4; i++) {
    ctx.fillStyle = i % 2 ? "#4e3c28" : "#62503a"; ctx.fillRect(0, (i * H) / 4, W, H / 4 - 3);
  }
  for (let i = 0; i < 400; i++) {
    ctx.fillStyle = `rgba(20,12,6,${R() * 0.25})`;
    ctx.fillRect(R() * W, R() * H, 2 + R() * 30, 1 + R() * 2);
  }
  ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.font = `900 52px ${FONT}`;
  ctx.fillStyle = "rgba(232,213,163,0.62)";
  ctx.fillText("THE UNSTABLE", W / 2, H * 0.36);
  ctx.fillText("PIERRE", W / 2, H * 0.68);
  // flaked paint
  ctx.globalCompositeOperation = "destination-over";
  ctx.globalCompositeOperation = "source-atop";
  for (let i = 0; i < 160; i++) {
    ctx.fillStyle = "rgba(78,60,40,0.9)";
    ctx.fillRect(R() * W, R() * H, 3 + R() * 10, 2 + R() * 5);
  }
  ctx.globalCompositeOperation = "source-over";
}, 256);

export function WeatheredSign({ n }) {
  const mats = useMemo(() => ({
    post: new THREE.MeshStandardMaterial({ map: timberTex(), color: "#5a4632", roughness: 1 }),
    board: new THREE.MeshStandardMaterial({ map: oldSignTex(), roughness: 1 }),
    lamp: new THREE.MeshBasicMaterial({ color: "#ffb84d", toneMapped: false }),
  }), []);
  const lamp = useRef();
  useTick((t) => {
    // a tired lantern — it gutters, it does not swell
    const f = Math.sin(t * 9) + Math.sin(t * 23) * 0.5 > 1.1 ? 0.35 : 1;
    mats.lamp.color.setRGB(1 * f, 0.72 * f, 0.3 * f);
  }, [mats]);
  const glows = useMemo(() => [{ x: n.x + 0.05, y: 1.95, z: n.y + 0.55, color: "#ffb84d", size: 0.8 }], [n.x, n.y]);
  return (
    <group>
      <group position={[n.x, 0, n.y]} rotation={[0, Math.PI / 2, 0]}>
        <mesh position={[0, 0.95, 0]} rotation={[0, 0, 0.04]} material={mats.post}>
          <boxGeometry args={[0.14, 1.9, 0.14]} />
        </mesh>
        <mesh position={[0.02, 1.45, 0.1]} rotation={[0, 0, -0.09]} material={mats.board}>
          <boxGeometry args={[1.25, 0.62, 0.06]} />
        </mesh>
        <mesh ref={lamp} position={[-0.55, 1.95, 0.05]} material={mats.lamp}>
          <boxGeometry args={[0.1, 0.14, 0.1]} />
        </mesh>
      </group>
      <Glows points={glows} size={1.2} />
    </group>
  );
}

// ════════════════════════════════════════════════════════════════════
// ⭐ THE GATE — the grand welcome at the landward end
// ────────────────────────────────────────────────────────────────────
// Two tall posts and a lintel over the west end of the spine, with the
// board the 2D pier always had: WELCOME TO W'ILY'S PIER, and faintly,
// beneath the fresh paint, the old name struck through.
// ════════════════════════════════════════════════════════════════════
const gateTex = () => canvasTex(`bw-gate|${FONT_GEN}`, 1024, (ctx, W, H) => {
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, "#1a1030"); g.addColorStop(1, "#090514");
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = "#c9a84c"; ctx.lineWidth = 10; ctx.strokeRect(12, 12, W - 24, H - 24);
  ctx.strokeStyle = "#c9a84c55"; ctx.lineWidth = 3; ctx.strokeRect(30, 30, W - 60, H - 60);
  for (let i = 0; i < 30; i++) {
    const x = 36 + (i * (W - 72)) / 29;
    for (const y of [14, H - 14]) {
      ctx.beginPath(); ctx.arc(x, y, 7, 0, 7);
      ctx.fillStyle = i % 3 === 0 ? "#7ce7ff" : "#f0d060";
      ctx.shadowColor = ctx.fillStyle; ctx.shadowBlur = 16; ctx.fill(); ctx.shadowBlur = 0;
    }
  }
  ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.font = `700 40px ${FONT}`; ctx.fillStyle = "#d4c9b8";
  ctx.fillText("♥  WELCOME TO  ♥", W / 2, H * 0.24);
  ctx.font = `900 118px ${FONT}`; ctx.fillStyle = "#f0d060";
  ctx.shadowColor = "#f0d060"; ctx.shadowBlur = 30;
  ctx.fillText("W'ILY'S PIER", W / 2, H * 0.53);
  ctx.shadowBlur = 0;
  ctx.font = `italic 600 34px ${FONT}`; ctx.fillStyle = "rgba(154,138,122,0.6)";
  ctx.fillText("THE UNSTABLE PIERRE", W / 2, H * 0.8);
  const tw = ctx.measureText("THE UNSTABLE PIERRE").width;
  ctx.fillRect(W / 2 - tw / 2 - 6, H * 0.8 - 2, tw + 12, 3);
}, 320);

// The gate stands over the boardwalk's west end. Where and how wide is the
// plan's (GATE in pierPlan.js), so a wider boardwalk gets a wider gate.
export function PierGate({ world }) {
  const g = world.gate || { x: -0.62, z0: 7.35, z1: 12.65 };
  const gx = g.x, z0 = g.z0, z1 = g.z1;
  const zc = (z0 + z1) / 2, span = z1 - z0;
  const built = useMemo(() => {
    const m = new Merger();
    for (const z of [z0, z1]) {
      m.box("post", 0.46, 4.9, 0.46, [gx, 2.45, z]);
      m.box("cap", 0.66, 0.18, 0.66, [gx, 4.98, z]);
      m.cyl("cap", 0, 0.34, 0.5, 4, [gx, 5.32, z], [0, Math.PI / 4, 0]);
      m.box("cap", 0.6, 0.3, 0.6, [gx, 0.15, z]);
    }
    m.box("post", 0.32, 0.34, span + 0.6, [gx, 4.5, (z0 + z1) / 2]);
    m.box("post", 0.24, 0.2, span, [gx, 2.7, (z0 + z1) / 2]);
    const geos = m.build();
    if (geos.post) boxUV(geos.post, 1.2);
    return geos;
  }, [gx, z0, z1, span]);
  const mats = useMemo(() => ({
    post: new THREE.MeshStandardMaterial({ map: timberTex(), color: "#6a4e34", roughness: 0.9, emissive: "#2a1a0e", emissiveIntensity: 0.4 }),
    cap: new THREE.MeshStandardMaterial({ color: "#c9a84c", metalness: 0.8, roughness: 0.35, emissive: "#4a3208", emissiveIntensity: 0.5 }),
    board: new THREE.MeshBasicMaterial({ map: gateTex(), toneMapped: false, side: THREE.FrontSide }),
  }), []);
  useEffect(() => () => disposeGeos(built), [built]);
  const glows = useMemo(() => [z0, z1].map((z) => ({ x: gx, y: 5.25, z, color: "#f0d060", size: 1.2 })), [gx, z0, z1]);
  const boardW = Math.min(span - 0.9, 6.8);
  return (
    <group>
      <Merged geos={built} mats={mats} />
      {[Math.PI / 2, -Math.PI / 2].map((ry) => (
        <mesh key={ry} position={[gx + (ry > 0 ? 0.18 : -0.18), 3.62, zc]} rotation={[0, ry, 0]} material={mats.board}>
          <planeGeometry args={[boardW, boardW / 3.2]} />
        </mesh>
      ))}
      <Glows points={glows} size={2} />
    </group>
  );
}

// ════════════════════════════════════════════════════════════════════
// THE FAR END — something dark stands where the pier stops
// ────────────────────────────────────────────────────────────────────
// It does not advertise. It waits. A tapered monolith with a violet
// fracture in the face that looks back down the pier, and the only light
// it permits: two wisps, turning.
// ════════════════════════════════════════════════════════════════════
const crackTex = () => canvasTex("bw-crack", 128, (ctx, W, H) => {
  ctx.clearRect(0, 0, W, H);
  const R = mulberry32(0xFA2E);
  ctx.strokeStyle = "#b388ff"; ctx.lineWidth = 3; ctx.shadowColor = "#b388ff"; ctx.shadowBlur = 12;
  ctx.beginPath();
  let x = W * 0.5, y = H * 0.04;
  ctx.moveTo(x, y);
  while (y < H * 0.96) { x += (R() - 0.5) * 18; y += 10 + R() * 14; ctx.lineTo(Math.max(10, Math.min(W - 10, x)), y); }
  ctx.stroke();
}, 256);

export function Monolith({ n }) {
  const mats = useMemo(() => ({
    stone: new THREE.MeshStandardMaterial({ color: "#0e0a1a", roughness: 0.35, metalness: 0.5, emissive: "#140a26", emissiveIntensity: 0.6 }),
    crack: new THREE.MeshBasicMaterial({ map: crackTex(), transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false }),
    pool: new THREE.MeshBasicMaterial({ map: poolTex(), color: "#6d5aa8", transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false, opacity: 0.75 }),
    shard: new THREE.MeshStandardMaterial({ color: "#1a1030", emissive: "#8a60ff", emissiveIntensity: 0.9, roughness: 0.3, metalness: 0.4, toneMapped: false }),
  }), []);
  const wisps = useRef();
  useTick((t) => {
    mats.crack.opacity = 0.55 + 0.45 * Math.sin(t * 0.9);
    // the crystals in the fracture breathe with it
    mats.shard.emissiveIntensity = 0.7 + 0.5 * Math.sin(t * 0.9 + 1);
    if (wisps.current) {
      const a = wisps.current.geometry.attributes.position;
      a.setXYZ(0, n.x + Math.cos(t * 0.7) * 0.7, 1.6 + Math.sin(t * 0.5) * 0.3, n.y + Math.sin(t * 0.7) * 0.7);
      a.setXYZ(1, n.x + Math.cos(-t * 0.45 + 2) * 1.0, 2.3 + Math.sin(t * 0.4 + 1) * 0.4, n.y + Math.sin(-t * 0.45 + 2) * 1.0);
      a.needsUpdate = true;
    }
  }, [mats, n.x, n.y]);
  const wGeo = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(6), 3));
    return g;
  }, []);
  const wMat = useMemo(() => new THREE.PointsMaterial({
    size: 0.35, map: dotTex(), color: "#8a70d8", transparent: true, depthWrite: false,
    blending: THREE.AdditiveBlending, toneMapped: false, fog: false,
  }), []);
  useEffect(() => () => { wGeo.dispose(); wMat.dispose(); }, [wGeo, wMat]);
  return (
    <group>
      <group position={[n.x, 0, n.y]}>
        <mesh position={[0, 0.07, 0]} rotation={[0, Math.PI / 8, 0]} material={mats.stone}>
          <cylinderGeometry args={[0.95, 1.05, 0.14, 8]} />
        </mesh>
        <mesh position={[0, 2.04, 0]} rotation={[0, Math.PI / 4, 0]} material={mats.stone}>
          <cylinderGeometry args={[0.24, 0.5, 3.8, 4]} />
        </mesh>
        {/* ⚠ The fracture was on ONE face, the west one, so from the street
            camera (south) the Far End read as a plain dark post. It runs down
            every face now: it looks back at you from wherever you stand. */}
        {[0, Math.PI / 2, Math.PI, -Math.PI / 2].map((ry) => (
          <group key={ry} rotation={[0, ry, 0]}>
            {/* ⚠ 0.37 out, not 0.3: the obelisk tapers, and at 0.3 the lower
                half of every fracture was buried inside the stone */}
            <mesh position={[0, 2.0, 0.37]} rotation={[-0.048, 0, 0]} material={mats.crack}>
              <planeGeometry args={[0.46, 3.4]} />
            </mesh>
          </group>
        ))}
        {/* ⚠ CRYSTALS GROWING OUT OF THE FRACTURE, half-buried in the stone.
            Three splinters circling it read as floating diamonds, twice
            (visual reviews, 2026-09-14). [x, y, z, tiltX, tiltZ, height] */}
        {[[0.1, 1.15, 0.3, 0.5, 0.2, 0.26], [0.26, 2.05, -0.06, 0.2, 0.9, 0.2],
          [-0.22, 2.85, 0.05, -0.4, -0.5, 0.17], [-0.05, 0.5, -0.38, 0.3, 0.2, 0.22]].map(([sx, sy, sz, rx, rz, h], i) => (
          <mesh key={`s${i}`} position={[sx, sy, sz]} rotation={[rx, i, rz]} scale={[h * 0.42, h, h * 0.42]} material={mats.shard}>
            <octahedronGeometry args={[1, 0]} />
          </mesh>
        ))}
        <mesh position={[0, 0.16, 0]} rotation={[-Math.PI / 2, 0, 0]} material={mats.pool}>
          <planeGeometry args={[4.6, 4.6]} />
        </mesh>
      </group>
      <points ref={wisps} geometry={wGeo} material={wMat} frustumCulled={false} />
    </group>
  );
}
